import { Injectable, computed, inject, linkedSignal, signal } from '@angular/core';

import { UserPreferencesService } from './user-preferences.service';
import { UserSettingsService } from './user-settings.service';

export type LayoutMode = 'mobile' | 'tablet' | 'compact' | 'desktop';

const TABLET_QUERY = '(min-width: 768px) and (max-width: 1023.98px)';
const COMPACT_QUERY = '(min-width: 1024px) and (max-width: 1599.98px)';
const DESKTOP_QUERY = '(min-width: 1600px)';

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly preferences = inject(UserPreferencesService);
  private readonly settings = inject(UserSettingsService);

  /** Starts from the remembered state when the user has asked for it, otherwise expanded. */
  private readonly _collapsed = linkedSignal<boolean>(
    () => this.settings.appearance().rememberSidebar && this.preferences.select('sidebarCollapsed')(),
  );
  private readonly _mobileOpen = signal(false);
  private readonly _mode = signal<LayoutMode>(this.detectMode());

  readonly collapsed = this._collapsed.asReadonly();
  readonly mobileOpen = this._mobileOpen.asReadonly();
  readonly mode = this._mode.asReadonly();

  readonly isOverlay = computed(() => this._mode() !== 'desktop');
  readonly overlayVisible = computed(() => this.isOverlay() && this._mobileOpen());
  readonly isFloating = computed(() => {
    const m = this._mode();
    return m === 'mobile' || m === 'tablet';
  });

  constructor() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const tablet = window.matchMedia(TABLET_QUERY);
    const compact = window.matchMedia(COMPACT_QUERY);
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const sync = () => {
      const next = this.detectMode();
      this._mode.set(next);
      if (next === 'desktop') this._mobileOpen.set(false);
    };
    tablet.addEventListener('change', sync);
    compact.addEventListener('change', sync);
    desktop.addEventListener('change', sync);
  }

  toggle(): void {
    if (this.isOverlay()) {
      this._mobileOpen.update((v) => !v);
      return;
    }
    this.set(!this._collapsed());
  }

  set(collapsed: boolean): void {
    this._collapsed.set(collapsed);
    if (this.settings.appearance().rememberSidebar) {
      this.preferences.set('sidebarCollapsed', collapsed);
    }
  }

  openOverlay(): void {
    if (this.isOverlay()) this._mobileOpen.set(true);
  }

  closeOverlay(): void {
    this._mobileOpen.set(false);
  }

  private detectMode(): LayoutMode {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
    if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
    if (window.matchMedia(COMPACT_QUERY).matches) return 'compact';
    if (window.matchMedia(TABLET_QUERY).matches) return 'tablet';
    return 'mobile';
  }
}
